import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useWebNfc — thin wrapper around the browser's NDEFReader (Web NFC) API.
 *
 * The API is only available in Chrome/Edge on Android over HTTPS. This hook
 * degrades gracefully on unsupported browsers so callers can render a QR
 * fallback.
 *
 * Returns:
 *   supported   : boolean         — is NDEFReader available at all?
 *   scanning    : boolean         — is the reader actively listening?
 *   lastRecord  : { serialNumber, text, url, raw } | null
 *   error       : string | null
 *   startScan() : Promise<void>   — start listening. Rejects if permission denied.
 *   stopScan()  : void            — abort the current scan
 *   writeUrl(url:string, options?): Promise<void>
 *   writeText(text:string, options?): Promise<void>
 */
export default function useWebNfc() {
  const [supported]   = useState(() => typeof window !== "undefined" && "NDEFReader" in window);
  const [scanning, setScanning] = useState(false);
  const [lastRecord, setLastRecord] = useState(null);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const readerRef     = useRef(null);

  const stopScan = useCallback(() => {
    try { controllerRef.current?.abort(); } catch { /* ignore */ }
    controllerRef.current = null;
    setScanning(false);
  }, []);

  const startScan = useCallback(async () => {
    if (!supported) { setError("Web NFC isn't supported on this device — use Chrome for Android."); return; }
    setError(null);
    try {
      const NDEFReader = window.NDEFReader;
      const reader = new NDEFReader();
      readerRef.current = reader;
      const ctrl = new AbortController();
      controllerRef.current = ctrl;
      await reader.scan({ signal: ctrl.signal });
      setScanning(true);
      reader.onreadingerror = (e) => setError(String(e?.message || e || "read_error"));
      reader.onreading = ({ message, serialNumber }) => {
        // A single NDEF message can carry multiple records. We surface the
        // first `url` or `text` record we find — that's how most tags are
        // written.
        const rec = { serialNumber: serialNumber || null, text: null, url: null, raw: message };
        try {
          for (const r of message.records) {
            const dec = new TextDecoder(r.encoding || "utf-8");
            const payload = dec.decode(r.data);
            if (r.recordType === "url" || r.recordType === "absolute-url") {
              rec.url = payload; break;
            }
            if (r.recordType === "text") {
              // Text records prefix the payload with a language code byte + code.
              rec.text = payload.replace(/^[\x00-\x1F].{0,2}/, "");
            }
          }
        } catch (e) { setError(String(e?.message || e)); }
        setLastRecord(rec);
      };
    } catch (e) {
      const msg = e?.name === "NotAllowedError" ? "NFC permission blocked — allow it in browser settings." :
                  e?.name === "NotSupportedError" ? "This device has no NFC chip." :
                  String(e?.message || e || "start_failed");
      setError(msg);
      setScanning(false);
    }
  }, [supported]);

  const writeUrl = useCallback(async (url, { overwrite = true } = {}) => {
    if (!supported) throw new Error("Web NFC not supported");
    const NDEFReader = window.NDEFReader;
    const w = new NDEFReader();
    await w.write({ records: [{ recordType: "url", data: url }] }, { overwrite });
  }, [supported]);

  const writeText = useCallback(async (text, { overwrite = true } = {}) => {
    if (!supported) throw new Error("Web NFC not supported");
    const NDEFReader = window.NDEFReader;
    const w = new NDEFReader();
    await w.write({ records: [{ recordType: "text", data: text }] }, { overwrite });
  }, [supported]);

  useEffect(() => () => stopScan(), [stopScan]);

  return { supported, scanning, lastRecord, error, startScan, stopScan, writeUrl, writeText };
}
